import React, { useState, useEffect } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import DatePicker from '../components/DatePicker.jsx';
import Message from '../components/Message.jsx';
import PermissionGuard from "../components/PermissionGuard.jsx";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";
import CommonTable from '../components/common/CommonTable.jsx';
function Leave() {
  const { state, loadLeaves } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveData, setLeaveData] = useState({
    worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: ''
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loadingLeaves, setLoadingLeaves] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Processing...");

  const [showFilters, setShowFilters] = useState(false);

  const [leaveFilters, setLeaveFilters] = useState({
    worker_id: "",
    worker_name: "",
    status: "",
    type: "",
    session: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Check required fields
    if (!leaveData.startDate) {
      setMessage({ text: 'From date is required!', type: 'error' });
      return;
    }
    if (!leaveData.endDate) {
      setMessage({ text: 'To date is required!', type: 'error' });
      return;
    }

    // Validation conditions
    const startDate = new Date(leaveData.startDate);
    const endDate = new Date(leaveData.endDate);
    const today = new Date();

    if (startDate < today.setHours(0, 0, 0, 0)) {
      setMessage({ text: 'Start date cannot be in the past!', type: 'error' });
      return;
    }

    if (endDate < startDate) {
      setMessage({ text: 'To date should be same or after from date!', type: 'error' });
      return;
    }

    const leaveDays = calculateDays(leaveData.startDate, leaveData.endDate || leaveData.startDate);
    const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveData.type);

    let experienceWarning = '';
    let maxDays = selectedLeaveType ? selectedLeaveType.maxPerYear : 30;

    // Check experience eligibility if leave type is experience-based
    if (selectedLeaveType && selectedLeaveType.byExperience) {
      const experienceCheck = checkWorkerExperience(leaveData.worker_id, selectedLeaveType);
      if (!experienceCheck.eligible) {
        experienceWarning = `Note: You have ${experienceCheck.experienceYears} years experience but this leave type requires ${experienceCheck.requiredRange}. `;
      } else {
        maxDays = experienceCheck.maxPerYear || maxDays;
      }
    }

    // Show warning if exceeding limit
    if (leaveDays > maxDays) {
      setMessage({
        text: `Warning: You are applying for ${leaveDays} days but the limit for ${leaveData.type} is ${maxDays} days per year. ${experienceWarning}Application will be submitted for admin review.`,
        type: "warning",
      });

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    // Show experience warning
    if (experienceWarning && leaveDays <= maxDays) {
      setMessage({
        text: `Warning: ${experienceWarning}Application will be submitted for admin review.`,
        type: "warning",
      });

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    if (leaveData.reason.length < 1) {
      setMessage({ text: 'Please provide a detailed reason (minimum 1 characters)', type: 'error' });
      return;
    }

    const existingLeaveSameDay = (state.leaves || []).find(leave =>
      leave.workerId === leaveData.worker_id &&
      new Date(leave.startDt).toDateString() === new Date(leaveData.startDate).toDateString()
    );

    if (existingLeaveSameDay) {
      setMessage({
        text: "Leave already applied for this date!",
        type: "error"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const leavePayload = {
        workerId: leaveData.worker_id,
        graceperiodId: state.gracePeriods && state.gracePeriods.length > 0 ? state.gracePeriods[0].id : null,
        type: leaveData.type,
        startDt: leaveData.startDate,
        endDt: leaveData.endDate || leaveData.startDate,
        session: leaveData.session,
        reason: leaveData.reason,
        leaveStatus: 0
      };

      await api.leaves.create(leavePayload);
      setMessage({ text: 'Leave application submitted successfully!', type: 'success' });
      await loadLeaves();
      setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
      setShowModal(false);
    } catch (error) {
      console.error('Error creating leave:', error);
      setMessage({ text: error?.message || 'Error submitting leave application!', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkWorkerExperience = (worker_id, leaveType) => {
    const worker = (state.workers || []).find(w => w.id === worker_id);
    if (!worker || !leaveType.byExperience) return { eligible: true };

    // Use joinDate from worker object (could be joinDt or joinDate)
    const joinDate = new Date(worker.joinDate || worker.joinDt);
    if (!joinDate || isNaN(joinDate.getTime())) return { eligible: true };

    const today = new Date();
    const experienceYears = (today - joinDate) / (1000 * 60 * 60 * 24 * 365.25);

    // Handle multiple experience ranges
    if (leaveType.experienceRanges && leaveType.experienceRanges.length > 0) {
      const applicableRange = leaveType.experienceRanges.find(range => {
        const fromYears = range.experienceFrom || 0;
        const toYears = range.experienceTo;
        return experienceYears >= fromYears && (toYears ? experienceYears <= toYears : true);
      });

      if (applicableRange) {
        return {
          eligible: true,
          experienceYears: Math.floor(experienceYears * 10) / 10,
          requiredRange: applicableRange.experienceTo ? `${applicableRange.experienceFrom}-${applicableRange.experienceTo} years` : `${applicableRange.experienceFrom}+ years`,
          maxPerYear: applicableRange.experienceMaxPerYear
        };
      } else {
        // Find the closest range for error message
        const sortedRanges = leaveType.experienceRanges.sort((a, b) => (a.experienceFrom || 0) - (b.experienceFrom || 0));
        const firstRange = sortedRanges[0];
        return {
          eligible: false,
          experienceYears: Math.floor(experienceYears * 10) / 10,
          requiredRange: firstRange.experienceTo ? `${firstRange.experienceFrom}-${firstRange.experienceTo} years` : `${firstRange.experienceFrom}+ years`,
          maxPerYear: 0
        };
      }
    } else {
      // Fallback to old single range format
      const fromYears = leaveType.experienceFrom || 0;
      const toYears = leaveType.experienceTo || 999;
      const eligible = experienceYears >= fromYears && (leaveType.experienceTo ? experienceYears <= toYears : true);

      return {
        eligible,
        experienceYears: Math.floor(experienceYears * 10) / 10,
        requiredRange: leaveType.experienceTo ? `${fromYears}-${toYears} years` : `${fromYears}+ years`,
        maxPerYear: leaveType.experienceMaxPerYear || leaveType.maxPerYear
      };
    }
  };

  const handleViewDetails = async (leave) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading leave details...");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setSelectedLeave(leave);
      setShowInfoModal(true);
    } catch (error) {
      console.error("Error opening leave details:", error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleStatusChange = async (leaveId, newStatus) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText(newStatus === 'Approved' ? "Approving leave..." : "Rejecting leave...");
    try {
      if (newStatus === 'Approved') {
        const leaveToApprove = (state.leaves || []).find(leave => leave.id === leaveId);
        if (leaveToApprove) {
          const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveToApprove.type);

          if (selectedLeaveType) {
            // Check experience eligibility
            const experienceCheck = checkWorkerExperience(leaveToApprove.workerId, selectedLeaveType);

            if (!experienceCheck.eligible) {
              setMessage({
                text: `Warning: Worker has ${experienceCheck.experienceYears} years of experience but this leave type requires ${experienceCheck.requiredRange} experience. Leave approved with admin override.`,
                type: 'warning'
              });
            }

            // Check leave limit and show warning
            const maxDays = experienceCheck.maxPerYear || selectedLeaveType.maxPerYear || 30;
            const workerLeaveBreakdown = getLeaveTypeBreakdown(leaveToApprove.workerId);
            const currentTypeLeaves = workerLeaveBreakdown[leaveToApprove.type] || 0;
            const leaveDays = calculateDays(leaveToApprove.startDt, leaveToApprove.endDt);

            if (currentTypeLeaves + leaveDays > maxDays) {
              setMessage({
                text: `Warning: Worker has already taken ${currentTypeLeaves} days for ${leaveToApprove.type} leave type. This approval will exceed the limit of ${maxDays} days per year by ${(currentTypeLeaves + leaveDays) - maxDays} days. Leave approved with admin override.`,
                type: 'warning'
              });
            }
          }
        }
      }

      const statusCode = newStatus === 'Approved' ? 1 : 2;
      await api.leaves.updateStatus(leaveId, statusCode);
      await loadLeaves();
      setMessage({ text: `Leave ${newStatus.toLowerCase()} successfully!`, type: 'success' });
    } catch (error) {
      console.error('Error updating leave status:', error);
      setMessage({ text: 'Error updating leave status!', type: 'error' });
    } finally {
      setIsActionLoading(false);
    }
  };

  const getWorkerName = (leave) => {
    // First try to get from association
    if (leave.worker && leave.worker.name) {
      return leave.worker.name;
    }
    // Fallback to finding in workers array using workerId
    if (state.workers && Array.isArray(state.workers)) {
      const worker = state.workers.find(worker => worker.id === leave.workerId);
      return worker ? worker.name : 'Unknown';
    }
    return 'Unknown';
  };

  const getTotalPreviousLeaves = (worker_id) => {
    if (!state.leaves || !Array.isArray(state.leaves)) return 0;
    return state.leaves.filter(leave =>
      leave.workerId === worker_id && leave.leaveStatus === 1 // Only approved leaves
    ).reduce((total, leave) => {
      if (leave.session === 'First Half' || leave.session === 'Second Half') {
        return total + 0.5;
      }
      return total + calculateDays(leave.startDt, leave.endDt);
    }, 0);
  };

  const getLeaveTypeBreakdown = (worker_id) => {
    if (!state.leaves || !Array.isArray(state.leaves)) return {};

    const approvedLeaves = state.leaves.filter(leave =>
      leave.workerId === worker_id && leave.leaveStatus === 1
    );

    const breakdown = {};
    approvedLeaves.forEach(leave => {
      const leaveType = leave.type;
      const days = (leave.session === 'First Half' || leave.session === 'Second Half')
        ? 0.5
        : calculateDays(leave.startDt, leave.endDt);

      if (breakdown[leaveType]) {
        breakdown[leaveType] += days;
      } else {
        breakdown[leaveType] = days;
      }
    });

    return breakdown;
  };

  const formatLeaveBreakdown = (worker_id) => {
    const breakdown = getLeaveTypeBreakdown(worker_id);
    const entries = Object.entries(breakdown);

    if (entries.length === 0) return 'No leaves taken';

    return entries.map(([type, days]) => `${type}: ${days}`).join(', ');
  };

  const calculateDays = (startDate, endDate) => {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate || startDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  useEffect(() => {
    let isMounted = true;

    const loadPageData = async () => {
      try {
        setLoadingLeaves(true);
        setLoadError('');
        await loadLeaves();
      } catch (error) {
        console.error('Error loading leave data:', error);
        if (isMounted) {
          setLoadError(error.message || 'Unable to load leave data');
        }
      } finally {
        if (isMounted) {
          setLoadingLeaves(false);
        }
      }
    };

    loadPageData();

    return () => {
      isMounted = false;
    };
  }, [loadLeaves]);

  const filteredLeaves = (state.leaves || []).filter((leave) => {

    const workerName = getWorkerName(leave).toLowerCase();

    const leaveStatus =
      leave.leaveStatus === 1
        ? "Approved"
        : leave.leaveStatus === 2
          ? "Rejected"
          : "Pending";

    const matchesWorker =
      !leaveFilters.worker_id
        ? (!leaveFilters.worker_name || workerName.includes(leaveFilters.worker_name.toLowerCase()))
        : String(leave.workerId) === String(leaveFilters.worker_id);

    const matchesStatus =
      !leaveFilters.status ||
      leaveStatus === leaveFilters.status;

    const matchesType =
      !leaveFilters.type ||
      leave.type === leaveFilters.type;

    const matchesSession =
      !leaveFilters.session ||
      leave.session === leaveFilters.session;

    return (
      matchesWorker &&
      matchesStatus &&
      matchesType &&
      matchesSession
    );
  });

  // Handle browser back button to close modals
  useEffect(() => {
    const handlePopState = (event) => {
      if (showModal || showInfoModal) {
        event.preventDefault();
        event.stopPropagation();
        setShowModal(false);
        setShowInfoModal(false);
        setSelectedLeave(null);
        setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
      }
    };

    if (showModal || showInfoModal) {
      window.history.pushState({ page: 'leave', modalOpen: true }, '', '#leave-modal');
      window.addEventListener('popstate', handlePopState, true);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [showModal, showInfoModal]);

  return (
    <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.VIEW} submodule={MODULES.LEAVE.submodules.LEAVE_APPLICATIONS}>
      <div className="leave">
        {/* Full-screen blocking loader when action is triggered */}
        {isActionLoading && (
          <div
            className="full-screen-action-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              inset: 0,
              width: "100%",
              height: "100%",
              minWidth: "100%",
              minHeight: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <div
              className="full-screen-action-card"
              style={{
                background: "#ffffff",
                padding: "18px 28px",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                pointerEvents: "auto",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span
                className="full-screen-action-text"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#212529",
                  letterSpacing: "0.2px",
                }}
              >
                {actionLoadingText || "Processing..."}
              </span>
            </div>
          </div>
        )}

        {message.text && (
          <Message
            message={message.text}
            type={message.type}
            onClose={() => setMessage({ text: '', type: '' })}
          />
        )}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h2 className='hd_a mb-0'>Leave Management</h2>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn ${Object.values(leaveFilters).some(v => v !== "")
                ? "btn-primary"
                : "btn-outline-primary"
                }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="bi bi-funnel"></i>
            </button>
            <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.CREATE} submodule={MODULES.LEAVE.submodules.LEAVE_APPLICATIONS}>
              <button className="btn btn-primary" onClick={() => {
                setMessage({ text: '', type: '' });
                setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
                setShowModal(true);
              }}>
                <i className="bi bi-plus-lg me-1"></i>Apply Leave
              </button>
            </PermissionGuard>
          </div>
        </div>

        {showFilters && (
          <div className="card shadow-sm mb-3">
            <div className="card-body py-3">
              <div className="row g-3">

                <div className="col-md-3">
                  <label className="form-label">Worker/Employee Name</label>
                  <select
                    className="form-control"
                    value={leaveFilters.worker_id}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const matched = (state.workers || []).find((w) => String(w.id) === String(selectedId));
                      const cleanName = matched?.worker_name || matched?.name?.replace(/\s*\((sigi|sigipro)\)$/i, '') || "";
                      setLeaveFilters({
                        ...leaveFilters,
                        worker_id: selectedId,
                        worker_name: cleanName,
                      });
                    }}
                  >
                    <option value="">All Worker/Employee</option>
                    {(state.workers || []).map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control"
                    value={leaveFilters.status}
                    onChange={(e) =>
                      setLeaveFilters({
                        ...leaveFilters,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="">All</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-control"
                    value={leaveFilters.type}
                    onChange={(e) =>
                      setLeaveFilters({
                        ...leaveFilters,
                        type: e.target.value,
                      })
                    }
                  >
                    <option value="">All</option>

                    {(state.leaveTypes || []).map((leaveType) => (
                      <option
                        key={leaveType.id}
                        value={leaveType.leaveName}
                      >
                        {leaveType.leaveName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Session</label>
                  <select
                    className="form-control"
                    value={leaveFilters.session}
                    onChange={(e) =>
                      setLeaveFilters({
                        ...leaveFilters,
                        session: e.target.value,
                      })
                    }
                  >
                    <option value="">All</option>
                    <option value="Full Day">Full Day</option>
                    <option value="First Half">First Half</option>
                    <option value="Second Half">Second Half</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={() =>
                      setLeaveFilters({
                        worker_id: "",
                        worker_name: "",
                        status: "",
                        type: "",
                        session: "",
                      })
                    }
                  >
                    Clear
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', pointerEvents: isSubmitting ? 'none' : 'auto' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header bg-primary text-white pt-2 pr-0 pb-2">
                  <h5 className="modal-title">Apply for Leave</h5>
                  <button type="button" className="btn-close btn-close-white" disabled={isSubmitting} onClick={() => {
                    if (isSubmitting) return;
                    setMessage({ text: '', type: '' });
                    setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
                    setShowModal(false);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body p-2">
                    {message.text && (
                      <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type || 'info'} py-1 px-2 mb-2`}>
                        {message.text}
                      </div>
                    )}
                    <div className="row g-1">
                      <div className="col-3">
                        <label className="form-label fw-bold">Worker/Employee Name *</label>
                        <select className="form-select" value={leaveData.worker_id} onChange={(e) => { const val = e.target.value; setLeaveData({ ...leaveData, worker_id: val }); }} required>
                          <option value="">Select Worker</option>
                          {(state.workers || []).map(worker => (
                            <option key={worker.id} value={worker.id}>{worker.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-3">
                        <label className="form-label fw-bold">Leave Type *</label>
                        <select className="form-select" value={leaveData.type} onChange={(e) => setLeaveData({ ...leaveData, type: e.target.value })} required>
                          <option value="">Select Type</option>
                          {(state.leaveTypes || [])
                            .filter(
                              (leaveType) =>
                                (leaveType.status ?? 1) === 1 ||
                                leaveType.leaveName === leaveData.type
                            )
                            .map((leaveType) => (
                              <option key={leaveType.id} value={leaveType.leaveName}>
                                {leaveType.leaveName}
                              </option>
                            ))}
                        </select>
                        {leaveData.worker_id && leaveData.type && (() => {
                          const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveData.type);
                          if (selectedLeaveType) {
                            let maxDays;

                            if (selectedLeaveType.byExperience) {
                              const experienceCheck = checkWorkerExperience(leaveData.worker_id, selectedLeaveType);
                              if (selectedLeaveType.experienceRanges && selectedLeaveType.experienceRanges.length > 0) {
                                // Find the appropriate range for worker's experience
                                const worker = (state.workers || []).find(w => w.id === leaveData.worker_id);
                                if (worker) {
                                  const joinDate = new Date(worker.joinDate || worker.joinDt);
                                  const experienceYears = (new Date() - joinDate) / (1000 * 60 * 60 * 24 * 365.25);

                                  const applicableRange = selectedLeaveType.experienceRanges.find(range => {
                                    const fromYears = range.experienceFrom || 0;
                                    const toYears = range.experienceTo;
                                    return experienceYears >= fromYears && (toYears ? experienceYears <= toYears : true);
                                  });

                                  maxDays = applicableRange ? applicableRange.experienceMaxPerYear : 0;
                                } else {
                                  maxDays = selectedLeaveType.experienceRanges[0]?.experienceMaxPerYear || 0;
                                }
                              } else {
                                maxDays = selectedLeaveType.experienceMaxPerYear || 0;
                              }
                            } else {
                              maxDays = selectedLeaveType.maxPerYear || 30;
                            }

                            const currentTypeLeaves = getLeaveTypeBreakdown(leaveData.worker_id)[leaveData.type] || 0;
                            const remainingDays = Math.max(0, maxDays - currentTypeLeaves);
                            return (
                              <div>
                                <small className={`mt-1 d-block ${remainingDays === 0 ? 'text-danger' : remainingDays <= 2 ? 'text-warning' : 'text-info'}`}>
                                  Used: {currentTypeLeaves}/{maxDays} days | Remaining: {remainingDays} days
                                </small>
                                <small className={`d-block ${selectedLeaveType.paidUnpaid === 'Paid' ? 'text-success' : 'text-warning'}`}>
                                  Type: {selectedLeaveType.paidUnpaid} Leave
                                </small>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <div className="col-3">
                        <label className="form-label fw-bold">Session *</label>
                        <select className="form-select" value={leaveData.session} onChange={(e) => setLeaveData({ ...leaveData, session: e.target.value })} required>
                          <option value="">Select Session</option>
                          <option value="Full Day">Full Day</option>
                          <option value="First Half">First Half</option>
                          <option value="Second Half">Second Half</option>
                        </select>
                      </div>
                      <div className="col-3">
                        <label className="form-label fw-bold">From Date *</label>
                        <DatePicker
                          value={leaveData.startDate}
                          onChange={(date) => setLeaveData({ ...leaveData, startDate: date })}
                          placeholder="Select start date"
                          minDate={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div className="col-3">
                        <label className="form-label fw-bold">To Date *</label>
                        <DatePicker
                          value={leaveData.endDate}
                          onChange={(date) => setLeaveData({ ...leaveData, endDate: date })}
                          placeholder="Select end date"
                          minDate={leaveData.startDate || new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>
                      <div className="col-9">
                        <label className="form-label fw-bold">Reason/Remark *</label>
                        <textarea className="form-control" rows="2" value={leaveData.reason} onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })} required></textarea>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer bg-light">
                    <button type="submit" className="btn btn-success px-4" disabled={isSubmitting}>
                      {isSubmitting && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" disabled={isSubmitting} onClick={() => {
                      if (isSubmitting) return;
                      setMessage({ text: '', type: '' });
                      setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
                      setShowModal(false);
                    }}>Close</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          {/* <div className="card-header">
          <h5 className='fs-6 mb-0'>Leave Requests</h5>
        </div> */}
          <div className="card-body px-0 pt-0">
            <CommonTable
              dataSource={api.leaves.table}
              queryParams={{
                worker_id: leaveFilters.worker_id,
                workerId: leaveFilters.worker_id,
                worker_name: leaveFilters.worker_name,
                leaveStatus: leaveFilters.status === 'Approved' ? 1 : leaveFilters.status === 'Rejected' ? 2 : leaveFilters.status === 'Pending' ? 0 : '',
                type: leaveFilters.type,
                session: leaveFilters.session
              }}
              loading={loadingLeaves}
              error={loadError}
              tableClassName="table table-bordered table-sm fs-7"
              columns={[
                { key: 'workerName', label: 'Worker/Employee', sortable: true, sortKey: 'worker.worker_name', render: (value, leave) => getWorkerName(leave) },
                { key: 'type', label: 'Type', sortable: true, render: (leave) => { const value = leave.type; const leaveType = (state.leaveTypes || []).find(item => item.leaveName === value); return <>{value}{leaveType && <div><small className={`badge ${leaveType.paidUnpaid === 'Paid' ? 'bg-success' : 'bg-warning'} ms-1`}>{leaveType.paidUnpaid}</small></div>}</>; } },
                { key: 'startDt', label: 'Start Date', sortable: true },
                { key: 'endDt', label: 'End Date', sortable: true },
                { key: 'days', label: 'Days', sortable: false, render: (value, leave) => leave.session === 'First Half' || leave.session === 'Second Half' ? '0.5' : calculateDays(leave.startDt, leave.endDt || leave.startDt) },
                { key: 'previousLeave', label: 'Total Previous Leave', sortable: false, render: (value, leave) => <div className="d-flex flex-column gap-1"><span className="badge bg-info">{getTotalPreviousLeaves(leave.workerId)} days</span><small className="text-muted">{formatLeaveBreakdown(leave.workerId)}</small></div> },
                { key: 'session', label: 'Session', sortable: true },
                { key: 'reason', label: 'Reason', sortable: true },
                { key: 'leaveStatus', label: 'Status', sortable: true, render: (value, leave) => <span className={`badge ${value === 1 ? 'bg-success' : value === 2 ? 'bg-danger' : 'bg-warning'}`}>{value === 1 ? 'Approved' : value === 2 ? 'Rejected' : 'Pending'}</span> }
              ]}
              renderActions={(leave) => <div className="btn-group">
                <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.INFO}>
                  <button
                    title='View Details'
                    className="btn btn-sm btn-primary"
                    disabled={isActionLoading}
                    onClick={() => handleViewDetails(leave)}
                    style={{ fontSize: '13px', padding: '2px 5px' }}
                  >
                    <i className="bi bi-info-circle-fill"></i>
                  </button>
                </PermissionGuard>
                {leave.leaveStatus === 0 && <>
                  <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.APPROVE}>
                    <button
                      title='Approve'
                      className="btn btn-sm btn-primary"
                      disabled={isActionLoading}
                      onClick={() => handleStatusChange(leave.id, 'Approved')}
                      style={{ fontSize: '13px', padding: '2px 5px' }}
                    >
                      <i className="bi bi-patch-check-fill"></i>
                    </button>
                  </PermissionGuard>
                  <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.REJECT}>
                    <button
                      title='Cancel'
                      className="btn btn-sm btn-danger"
                      disabled={isActionLoading}
                      onClick={() => handleStatusChange(leave.id, 'Rejected')}
                      style={{ fontSize: '13px', padding: '2px 5px' }}
                    >
                      <i className="bi bi-x-circle-fill"></i>
                    </button>
                  </PermissionGuard>
                </>}
              </div>}
              emptyMessage="No leaves found"
            />
          </div>
        </div>

        {showInfoModal && selectedLeave && (
          <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">Leave Details</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowInfoModal(false)}></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-6">
                      <strong>Worker/Employee:</strong> {getWorkerName(selectedLeave)}
                    </div>
                    <div className="col-6">
                      <strong>Leave Type:</strong> {selectedLeave.type}
                      {(() => {
                        const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === selectedLeave.type);
                        if (selectedLeaveType) {
                          return (
                            <span className={`badge ${selectedLeaveType.paidUnpaid === 'Paid' ? 'bg-success' : 'bg-warning'} ms-2`}>
                              {selectedLeaveType.paidUnpaid}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="col-6">
                      <strong>Start Date:</strong> {selectedLeave.startDt}
                    </div>
                    <div className="col-6">
                      <strong>End Date:</strong> {selectedLeave.endDt}
                    </div>
                    <div className="col-6">
                      <strong>Days:</strong> {calculateDays(selectedLeave.startDt, selectedLeave.endDt)}
                    </div>
                    <div className="col-12">
                      <strong>Total Previous Leave:</strong> <span className="badge bg-info">{getTotalPreviousLeaves(selectedLeave.workerId)} days</span>
                      <div className="mt-2">
                        <strong>Leave Type Breakdown:</strong>
                        <div className="mt-1">
                          {Object.entries(getLeaveTypeBreakdown(selectedLeave.workerId)).length > 0 ? (
                            Object.entries(getLeaveTypeBreakdown(selectedLeave.workerId)).map(([type, days]) => (
                              <span key={type} className="badge bg-secondary me-1 mb-1">{type}: {days} days</span>
                            ))
                          ) : (
                            <span className="text-muted">No previous leaves</span>
                          )}
                        </div>
                      </div>
                      {(() => {
                        const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === selectedLeave.type);
                        if (selectedLeaveType) {
                          const experienceCheck = checkWorkerExperience(selectedLeave.workerId, selectedLeaveType);
                          return (
                            <div className="mt-2">
                              <strong>Experience Eligibility:</strong>
                              <div className={`mt-1 p-2 rounded ${experienceCheck.eligible ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                                {experienceCheck.eligible ? (
                                  <span>✅ Eligible - Worker has {experienceCheck.experienceYears} years experience</span>
                                ) : (
                                  <span>⚠️ Not Eligible - Worker has {experienceCheck.experienceYears} years but requires {experienceCheck.requiredRange}</span>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="col-6">
                      <strong>Session:</strong> {selectedLeave.session}
                    </div>
                    <div className="col-12">
                      <strong>Status:</strong>
                      <span className={`badge ms-2 ${selectedLeave.leaveStatus === 1 ? 'bg-success' :
                        selectedLeave.leaveStatus === 2 ? 'bg-danger' : 'bg-warning'
                        }`}>
                        {selectedLeave.leaveStatus === 1 ? 'Approved' :
                          selectedLeave.leaveStatus === 2 ? 'Rejected' : 'Pending'}
                      </span>
                    </div>
                    <div className="col-12">
                      <strong>Reason:</strong>
                      <p className="mt-2 p-2 bg-light rounded">{selectedLeave.reason}</p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  {selectedLeave.leaveStatus === 0 && (
                    <div className="btn-group me-2">
                      <button
                        className="btn btn-success"
                        disabled={isActionLoading}
                        onClick={() => {
                          handleStatusChange(selectedLeave.id, 'Approved');
                          setShowInfoModal(false);
                        }}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        disabled={isActionLoading}
                        onClick={() => {
                          handleStatusChange(selectedLeave.id, 'Rejected');
                          setShowInfoModal(false);
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  <button type="button" className="btn btn-outline-secondary" onClick={() => {
                    setShowInfoModal(false);
                  }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PermissionGuard>
  );
}

export default Leave;
